import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { AllState } from '../../../../store'
import {
  MilestoneDetail,
  MilestoneInput,
} from '../../../../domain/entity/MilestoneEntity'
import { useMilestoneRepository } from '../../../../services/adaptors/milestoneRepositoryAdaptor'
import { featureFlagService } from '../../../../utils/feature-flag'
import Auth from '../../../../lib/commons/auth'

type UseMilestoneDataResult = {
  milestones: MilestoneDetail[]
  isLoadingMilestones: boolean
  fetchMilestones: () => void
  isMilestoneEnabled: boolean
  applyOptimisticAdd: (input: MilestoneInput) => void
  applyOptimisticEdit: (milestone: MilestoneDetail) => void
  applyOptimisticDelete: (uuid: string) => void
}

export const useMilestoneData = (): UseMilestoneDataResult => {
  const projectUuid = useSelector((state: AllState) => state.project.selected)
  const milestoneRepository = useMilestoneRepository()
  const [milestones, setMilestones] = useState<MilestoneDetail[]>([])
  const [isLoadingMilestones, setIsLoadingMilestones] = useState(false)

  const isMilestoneEnabled = featureFlagService.isEnabled('milestone')

  const fetchMilestones = useCallback(async () => {
    if (!projectUuid || !isMilestoneEnabled) return
    setIsLoadingMilestones(true)
    try {
      const data = await milestoneRepository.fetchAll()
      setMilestones(data)
    } finally {
      setIsLoadingMilestones(false)
    }
  }, [projectUuid, milestoneRepository, isMilestoneEnabled])

  useEffect(() => {
    fetchMilestones()
  }, [fetchMilestones])

  const applyOptimisticAdd = useCallback(
    (input: MilestoneInput) => {
      const user = Auth.getCurrentTenant()?.user
      if (!projectUuid || !user) return
      const now = Date.now()
      setMilestones(prev => [
        ...prev,
        {
          uuid: input.uuid,
          projectUuid,
          wbsItemUuid: input.wbsItemUuid,
          name: input.name,
          description: input.description,
          milestoneDate: input.milestoneDate,
          iconName: input.iconName,
          color: input.color,
          achieved: input.achieved ?? false,
          lockVersion: 1,
          revision: '',
          createdAt: now,
          createdBy: user,
          updatedAt: now,
          updatedBy: user,
        },
      ])
    },
    [projectUuid]
  )

  const applyOptimisticEdit = useCallback((milestone: MilestoneDetail) => {
    setMilestones(prev =>
      prev.map(m => (m.uuid === milestone.uuid ? milestone : m))
    )
  }, [])

  const applyOptimisticDelete = useCallback((uuid: string) => {
    setMilestones(prev => prev.filter(m => m.uuid !== uuid))
  }, [])

  return {
    milestones,
    isLoadingMilestones,
    fetchMilestones,
    isMilestoneEnabled,
    applyOptimisticAdd,
    applyOptimisticEdit,
    applyOptimisticDelete,
  }
}
